import i18n from '@/i18n'
import { eventService } from '@/services/event.service'
import { mapActions, mapGetters } from 'vuex'
import { EventHub } from '@/constants/constants'
import { DialogEvent, DialogStateType, DialogEventType } from "@/tools/dialog"
import { rgbToHex } from '@/tools/functions'
import { stateService } from '@/services/state.service'
import { clone } from 'ramda'
import AutocompleteComponent from "@/components/commons/autocomplete/AutocompleteComponent.vue";

export default {
    name: 'EventDialogComponent',
    components: {
        AutocompleteComponent
    },
    props: {
    },
    data: () => ({
        model: {},
        // model del formulario
        validForm: false,
        // reglas de validación
        validationRules: {
            required: (value) => !!value || value === 0 || `${i18n.t('required')}`,
            requiredState: (value, state) => !state || !!value || value === 0 || `${i18n.t('required')}`,
            noDuplicateEvent: (eventNumbers, value) => !eventNumbers.find(e => e === value) || `${i18n.t('error.duplicateEvent')}`
        },
        locale: null,
        eventTypes: [],
        stateTypes: [],
        disableState: false,
    }),
    computed: {
        ...mapGetters({
            'getBread': 'breadcrumbs/getBread',
        })
    },
    created() {
        this.locale = localStorage.getItem('locale')
        // Evento lanzado por el diálogo de creación de tipo de evento, cuando se guarda el nuevo tipo
        this.$eventBus.$on(EventHub.COMMIT_EVENT_TYPE, (eventTypeId) => {
            this.getEventTypes(eventTypeId)
        })
        // Evento lanzado por el diálogo de creación de tipo de estado, cuando se guarda el nuevo tipo
        this.$eventBus.$on(EventHub.COMMIT_STATE_TYPE, (stateTypeId) => {
            this.getStateTypes(stateTypeId)
        })

        /**
         * El modelo de datos del diálogo se carga a través del objeto del breadcrumb
         */
        this.unwatch = this.$store.watch(
            (state, getters) => getters['breadcrumbs/getBread'],
            (newBreads) => {
                const lastBreadcrumb = newBreads[newBreads.length - 1]
                if (!lastBreadcrumb) {
                    this.cancel()
                } else {
                    if (lastBreadcrumb.key === DialogEvent) {
                        if (lastBreadcrumb.model) {
                            this.model = clone(lastBreadcrumb.model)
                        }
                    }
                }
            },
            { deep: true })
    },
    beforeDestroy() {
        // desuscripción al bus de eventos
        this.$eventBus.$off(EventHub.COMMIT_EVENT_TYPE)
        this.$eventBus.$off(EventHub.COMMIT_STATE_TYPE)

        // elimino la instancia del watcher del breadcrumb
        this.unwatch()
    },
    mounted() {
        const lastBreadcrumb = this.getBread[this.getBread.length - 1]
        if (!lastBreadcrumb) {
            this.cancel()
        } else {
            if (lastBreadcrumb.key === DialogEvent) {
                if (lastBreadcrumb.model) {
                    this.model = clone(lastBreadcrumb.model)
                }
            }
        }
        this.getEventTypes()
        this.getStateTypes()
    },
    methods: {
        /**
         * actions del snackbar
         */
        ...mapActions('snackbar', [
            'showSnackbar',
            'closeSnackbar'
        ]),
        /**
         * actions del dialog
         */
        ...mapActions('dialog', [
            'openDialog',
            'closeDialog'
        ]),
        /**
         * actions del breadcrumb
         */
        ...mapActions('breadcrumbs', [
            'pullBread',
            'pushBread'
        ]),
        /**
         * Cancelar
         */
        cancel() {
            this.pullBread(DialogEvent)
            this.resetFields()
        },
        /**
         * Guardar
         */
        save() {
            const event = Object.assign({}, this.model)
            if (!event.stateType) {
                event.stateColor = undefined
                event.stateName = undefined
                event.stateType = undefined
            }
            delete event.eventNumbers
            delete event.editingIndex
            delete event.update
            if (this.model.update) {
                this.$eventBus.$emit(EventHub.UPDATE_EVENT, { event, index: this.model.editingIndex })
            } else {
                this.$eventBus.$emit(EventHub.COMMIT_EVENT, event)
            }
            this.cancel()
        },
        getEventTypes (eventType = null) {
            this.loadingEventTypes =  true
            const language = this.locale
            eventService.findAllForSelectorByEnterprise(this.model.enterpriseId || 'ONLY_GLOBAL').then(eventTypes => {
                this.eventTypes = eventTypes.sort(function (a, b)  {
                    return a.name[language].localeCompare(b.name[language], { ignorePunctuation: true })
                })
                if (eventType) {
                    this.model.eventType = eventType
                    this.changeEventType()
                }
                this.loadingEventTypes =  false
            })
        },
        getStateTypes (stateType = null) {
            this.loadingStateTypes =  true
            const language = this.locale
            stateService.findAllForSelectorByEnterprise(this.model.enterpriseId || 'ONLY_GLOBAL').then(statesTypes => {
                this.stateTypes = statesTypes.sort(function (a,b ) {
                    return a.name[language].localeCompare(b.name[language], { ignorePunctuation: true })
                })
                if (stateType) {
                    this.model.stateType = stateType
                    this.changeStateType()
                }
                this.loadingStateTypes = false
            })
        },
        changeEventType (event) {
            if (!this.model.update) {
                if (this.model.eventType) {
                    event = event || this.eventTypes.find(x => x._id === this.model.eventType)
                    this.model.eventName.es = event && event.name ? event.name.es : null
                    this.model.eventName.en = event && event.name ? event.name.en : null
                    this.model.eventName.pt = event && event.name ? event.name.pt : null
                    this.model.eventColor = event ? (event.color ? (event.color[0] === '#' ? event.color : rgbToHex(event.color)) : event.color) : null

                    /**
                     * Check for stopped for lack of empty box status
                     */
                    const lackOfEmptyId = this.eventTypes.find(x => x.name.es === 'Parada por falta de vacío')._id;
                    if(this.model.eventType === lackOfEmptyId) {
                        this.model.stateType = this.stateTypes.find(x => x.name.es === 'FALTA DE VACÍO')._id;
                        this.disableState = true;

                    } else {
                        this.model.stateType = null
                        this.disableState = false;
                    }
                    this.changeStateType();
                } else {
                    this.model.eventName.es = null
                    this.model.eventName.en = null
                    this.model.eventName.pt = null
                    this.model.eventColor = '#FF3333'
                }
            }
        },
        changeStateType () {
            if (!this.model.update) {
                if (this.model.stateType) {
                    const state = this.stateTypes.find(x => x._id === this.model.stateType)
                    this.model.stateName.es = state && state.name ? state.name.es : null
                    this.model.stateName.en = state && state.name ? state.name.en : null
                    this.model.stateName.pt = state && state.name ? state.name.pt : null
                    this.model.stateColor = state ? (state.color ? (state.color[0] === '#' ? state.color : rgbToHex(state.color)) : state.color) : null
                } else {
                    this.model.stateName.es = null
                    this.model.stateName.en = null
                    this.model.stateName.pt = null
                    this.model.stateColor = '#FF3333'
                }
            }
        },
        openEventType () {
            const bread = {
                key: DialogEventType,
                disabled: true,
                text: this.$t('eventType.name')
            }
            this.pushBread(bread)
        },
        openStateType () {
            const bread = {
                key: DialogStateType,
                disabled: true,
                text: this.$t('stateType.name')
            }
            this.pushBread(bread)
        },
        resetFields () {
            this.model = {
                eventNumber: null,
                eventType: null,
                eventName: {
                    es: null,
                    en: null,
                    pt: null
                },
                eventColor: '#FF3333',
                stateType: null,
                stateName: {
                    es: null,
                    en: null,
                    pt: null
                },
                stateColor: '#FF3333',
                eventNumbers: [],
                update: false
            }
            if (this.$refs.eventDialogForm) {
                this.$refs.eventDialogForm.reset()
            }
        },
        /**
         * Cada vez que se modifica la selección del tipo de evento, se cargan
         */
        eventTypeSelectedFunction (newValue) {
            this.model.eventType = newValue && newValue._id ? newValue._id : newValue;
            if (this.eventTypes && this.eventTypes.length !== 0) {
                this.changeEventType();
            }
        },
        /**
         * Cada vez que se modifica la selección del tipo de estado, se cargan
         */
        stateTypeSelectedFunction (newValue) {
            this.model.stateType = newValue && newValue._id ? newValue._id : newValue;
            if (this.stateTypes && this.stateTypes.length !== 0) {
                this.changeStateType();
            }
        },
    },
    watch: {
    }
}
